import type React from "react";

export enum BANNER_TYPES {
  FILTERS = "Filters",
  LINK_AND_IMAGE = "Link and Image",
  CATEGORIES = "Categories",
  FACETS = "Facets",
}

export type LayoutType = "scroll" | "wrap" | "grid";

export const TRANSPARENT_IMAGE =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";

// ---------------------------------------------------------------------------
// SFCC category shapes (from hec-composable filters/types.ts)
// ---------------------------------------------------------------------------

export type SfccCategoryEntry = {
  id: string;
  name: string;
  /**
   * Full multi-segment slug from the catalog root, joined with `/`
   * (e.g. `baby-and-mom/maternity-and-prenatal-care`). Computed at
   * fetch time from the parent category's `parentCategoryTree`.
   * Consumers should prefer this over `id` when building URLs — the
   * parent of a pill is not always the current PLP (e.g. when the user
   * is on an `all-X` view-all category, the pills show siblings rooted
   * under `X`'s actual parent, not the current pathname).
   */
  path?: string;
};

export type SfccCategory = {
  id: string;
  categories?: SfccCategoryEntry[];
  onlineSubCategoriesCount?: number;
  parentCategoryTree?: SfccCategoryEntry[];
};

export type NormalizedCategory = {
  id: string;
  name: string;
  categoryShortName: string;
  /**
   * Full multi-segment slug (see SfccCategoryEntry.path). The
   * CategoryFilters pill uses this for the `href`; absence falls back
   * to a `${pathname}/${categoryShortName}` heuristic that's wrong for
   * pills rendered from sibling categories.
   */
  path?: string;
  categoryIcon?: string;
  exclude?: boolean;
  iconRank?: number;
};

// ---------------------------------------------------------------------------
// Builder categoryEnrichment model shape (from hec-composable types/common.ts)
// ---------------------------------------------------------------------------

export interface ICategoryEnrichmentEntry {
  name?: string;
  data?: {
    categoryIcon?: string;
    categoryShortName?: string;
    iconRank?: number;
    excludeFromCategoryIconsBanner?: boolean;
  };
}

// ---------------------------------------------------------------------------
// Component props
// ---------------------------------------------------------------------------

export type CategoryFiltersProps = {
  attributes?: React.HTMLAttributes<HTMLDivElement>;
  className?: string;
  filterType?: BANNER_TYPES;
  list?: Array<Record<string, string>>;
  links?: Array<Record<string, string>>;
  facets?: Array<{ name: string; value: string; displayName?: string }>;
  layoutType?: LayoutType;
  subcategories?: NormalizedCategory[];
};
